import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { IconButton, TextField } from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosInstance';
import swal from '../../utils/swal';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { esRangoSinAtencion, horaNumeroATime, timeAHoraNumero } from '../../utils/horarios';
import Loader from '../../components/loading/Loader';
import './Disponibilidades.css';

const DIAS_SEMANA = [
    { diaSemana: 1, label: 'LUNES' },
    { diaSemana: 2, label: 'MARTES' },
    { diaSemana: 3, label: 'MIÉRCOLES' },
    { diaSemana: 4, label: 'JUEVES' },
    { diaSemana: 5, label: 'VIERNES' },
    { diaSemana: 6, label: 'SÁBADO' },
    { diaSemana: 0, label: 'DOMINGO' },
];

function construirBorrador(disponibilidades, sedeId) {
    const borrador = {};
    for (const { diaSemana } of DIAS_SEMANA) {
        const disp = disponibilidades.find(
            d => String(d.sedeId) === String(sedeId) && d.diaSemana === diaSemana
        );
        borrador[diaSemana] = disp
            ? { horaDesde: disp.horaDesde, horaHasta: disp.horaHasta }
            : null;
    }
    return borrador;
}

function construirEntradaBorrador(disponibilidades, sedeId, diaSemana) {
    const disp = disponibilidades.find(
        d => String(d.sedeId) === String(sedeId) && d.diaSemana === diaSemana
    );

    return disp ? { horaDesde: disp.horaDesde, horaHasta: disp.horaHasta } : null;
}

function Disponibilidades() {
    const { user } = useAuth();
    const medicoId = user?.medicoId;

    const [misSedes, setMisSedes] = useState([]);
    const [disponibilidades, setDisponibilidades] = useState([]);
    const [sedeActivaId, setSedeActivaId] = useState('');
    const [borrador, setBorrador] = useState({});
    const [diaEnEdicion, setDiaEnEdicion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [guardando, setGuardando] = useState(false);

    const cargarDatos = useCallback(async () => {
        if (!medicoId) return;

        setLoading(true);
        try {
            const [sedesRes, medicoRes] = await Promise.all([
                axiosInstance.get('/sedes'),
                axiosInstance.get(`/medicos/${medicoId}`),
            ]);

            const sedesIds = new Set((medicoRes.data.sedesIds ?? []).map(String));
            const sedesDelMedico = sedesRes.data.filter(s => sedesIds.has(String(s._id)));
            const dispos = medicoRes.data.disponibilidades ?? [];

            setMisSedes(sedesDelMedico);
            setDisponibilidades(dispos);
            setDiaEnEdicion(null);

            const primeraSede = sedesDelMedico[0]?._id ?? '';
            setSedeActivaId(prev => {
                const sigueValida = sedesDelMedico.some(s => String(s._id) === String(prev));
                const sedeId = sigueValida ? prev : primeraSede;
                setBorrador(construirBorrador(dispos, sedeId));
                return sedeId;
            });
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudieron cargar las disponibilidades. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setLoading(false);
        }
    }, [medicoId]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    function handleCambioSede(e) {
        const nuevaSedeId = e.target.value;
        setSedeActivaId(nuevaSedeId);
        setDiaEnEdicion(null);
        setBorrador(construirBorrador(disponibilidades, nuevaSedeId));
    }

    function handleEditar(diaSemana) {
        setDiaEnEdicion(diaSemana);
        setBorrador(prev => ({
            ...prev,
            [diaSemana]: construirEntradaBorrador(disponibilidades, sedeActivaId, diaSemana) ?? {
                horaDesde: null,
                horaHasta: null,
            },
        }));
    }

    function handleCancelar(diaSemana) {
        setBorrador(prev => ({
            ...prev,
            [diaSemana]: construirEntradaBorrador(disponibilidades, sedeActivaId, diaSemana),
        }));
        setDiaEnEdicion(null);
    }

    function handleCambioHorario(diaSemana, campo, timeValue) {
        setBorrador(prev => {
            const actual = prev[diaSemana] ?? { horaDesde: null, horaHasta: null };
            const valor = timeValue === '' ? null : timeAHoraNumero(timeValue);

            return {
                ...prev,
                [diaSemana]: { ...actual, [campo]: valor },
            };
        });
    }

    async function handleGuardarDia(diaSemana) {
        const draft = borrador[diaSemana];
        const originales = disponibilidades.filter(
            d => String(d.sedeId) === String(sedeActivaId)
        );
        const existente = originales.find(d => d.diaSemana === diaSemana);
        const esSinAtencion = esRangoSinAtencion(draft?.horaDesde, draft?.horaHasta);
        const tieneHorarios = !esSinAtencion && draft?.horaDesde != null && draft?.horaHasta != null;
        const incompleto = !esSinAtencion && ((draft?.horaDesde != null) !== (draft?.horaHasta != null));

        if (incompleto) {
            await swal.fire({
                icon: 'error',
                title: 'Horario incompleto',
                text: `Completá inicio y fin para ${DIAS_SEMANA.find(d => d.diaSemana === diaSemana)?.label ?? 'el día'}.`,
                confirmButtonColor: '#db1414',
            });
            return;
        }

        if (tieneHorarios) {
            if (draft.horaDesde >= draft.horaHasta) {
                await swal.fire({
                    icon: 'error',
                    title: 'Horario inválido',
                    text: 'La hora de inicio debe ser anterior a la de fin.',
                    confirmButtonColor: '#db1414',
                });
                return;
            }
        }

        if (!tieneHorarios && !esSinAtencion && !existente) {
            setDiaEnEdicion(null);
            return;
        }

        setGuardando(true);
        try {
            const payload = tieneHorarios
                ? {
                    disponibilidades: [{
                        sedeId: sedeActivaId,
                        diaSemana,
                        horaDesde: draft.horaDesde,
                        horaHasta: draft.horaHasta,
                    }],
                    eliminar: [],
                }
                : {
                    disponibilidades: [],
                    eliminar: [{ sedeId: sedeActivaId, diaSemana }],
                };

            const { data } = await axiosInstance.patch(
                `/medicos/${medicoId}/disponibilidad_horaria`,
                payload
            );
            const dispos = data.disponibilidades ?? [];
            setDisponibilidades(dispos);
            setBorrador(prev => ({
                ...prev,
                [diaSemana]: construirEntradaBorrador(dispos, sedeActivaId, diaSemana),
            }));
            setDiaEnEdicion(null);
            await swal.fire({
                icon: 'success',
                title: 'Disponibilidad guardada',
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (err) {
            const mensaje = getApiErrorMessage(
                err,
                'No se pudo guardar la disponibilidad. Intentá de nuevo.'
            );
            await swal.fire({ icon: 'error', title: 'Error', text: mensaje, confirmButtonColor: '#db1414' });
        } finally {
            setGuardando(false);
        }
    }

    if (!medicoId) {
        return (
            <p className="disponibilidades__mensaje">
                No se encontró el perfil de médico. Cerrá sesión e ingresá nuevamente.
            </p>
        );
    }

    if (loading) {
        return <Loader mensaje="Cargando disponibilidades..." />;
    }

    if (misSedes.length === 0) {
        return (
            <p className="disponibilidades__mensaje">
                Todavía no tenés sedes asignadas.{' '}
                <Link to="/sedes">Agregá una sede</Link> para configurar tu disponibilidad.
            </p>
        );
    }

    return (
        <div className="disponibilidades">
            <header className="disponibilidades__header">
                <h2 className="disponibilidades__title">Disponibilidad semanal</h2>
            </header>

            <div className="disponibilidades__sede-field">
                <label className="disponibilidades__sede-label" htmlFor="sede-select">
                    Sede
                </label>
                <select
                    id="sede-select"
                    className="disponibilidades__sede-select"
                    value={sedeActivaId}
                    onChange={handleCambioSede}
                >
                    {misSedes.map(sede => (
                        <option key={sede._id} value={sede._id}>
                            {sede.nombre}
                        </option>
                    ))}
                </select>
            </div>

            <div className="disponibilidades__grid">
                {DIAS_SEMANA.map(({ diaSemana, label }) => {
                    const draft = borrador[diaSemana];
                    const estaEditando = diaEnEdicion === diaSemana;
                    const valorDesde = horaNumeroATime(draft?.horaDesde);
                    const valorHasta = horaNumeroATime(draft?.horaHasta);

                    return (
                        <article key={diaSemana} className="disponibilidades__card">
                            <div className="disponibilidades__card-header">
                                <h3 className="disponibilidades__dia">{label}</h3>
                                {!estaEditando && (
                                    <IconButton
                                        className="disponibilidades__edit-icon-btn"
                                        size="small"
                                        onClick={() => handleEditar(diaSemana)}
                                        disabled={guardando}
                                    >
                                        <EditRounded fontSize="small" />
                                    </IconButton>
                                )}
                            </div>

                            {estaEditando ? (
                                <>
                                    <TextField
                                        label="Inicio"
                                        type="time"
                                        size="small"
                                        value={valorDesde}
                                        onChange={e => handleCambioHorario(diaSemana, 'horaDesde', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 1800 }}
                                        className="disponibilidades__time-field"
                                    />
                                    <TextField
                                        label="Fin"
                                        type="time"
                                        size="small"
                                        value={valorHasta}
                                        onChange={e => handleCambioHorario(diaSemana, 'horaHasta', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ step: 1800 }}
                                        className="disponibilidades__time-field"
                                    />
                                    <div className="disponibilidades__acciones-card">
                                        <button
                                            type="button"
                                            className="disponibilidades__btn disponibilidades__btn--cancelar"
                                            onClick={() => handleCancelar(diaSemana)}
                                            disabled={guardando}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="disponibilidades__btn disponibilidades__btn--confirmar"
                                            onClick={() => handleGuardarDia(diaSemana)}
                                            disabled={guardando}
                                        >
                                            {guardando ? <Loader inline /> : 'Guardar'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="disponibilidades__campo-label">Inicio</p>
                                    <p className="disponibilidades__campo-valor">{valorDesde || '—'}</p>
                                    <p className="disponibilidades__campo-label">Fin</p>
                                    <p className="disponibilidades__campo-valor">{valorHasta || '—'}</p>
                                </>
                            )}
                        </article>
                    );
                })}
            </div>
        </div>
    );
}

export default Disponibilidades;
